/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import { BUFFER_THRESHOLD_OFFSET } from '../../../config';
import { DeviceModel } from '../../../core/device';
import { Channel } from '../../../core/entities/Channel';
import { VodInteractor } from '../../../core/interactors';
import { Log } from '../../../core/logging/Log';
import { QoEAppLogger } from '../../../core/logging/qoe/QoEAppLogger';
import { VuLogger } from '../../../core/logging/VULogger';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { NetworkManager } from '../../../core/network/NetworkManager';
import { PlaybackContentType } from '../../../core/playback/enums';
import { PlaybackDataModelImpl } from '../../../core/playback/impls';
import { TChannelV2 } from '../../../core/types';
import { isJumpChannelCcbSwitchEnabled } from '../../../core/utils/ccb/CcbSwitchHelper';
import { IPlayable } from '../../../core/videoplayer/interfaces';
import { VideoPlaybackController } from '../../../core/videoplayer/VideoPlaybackController';
import { clearScheduleRecordingSharedPreference } from '../../overlay/recordingoverlay/recordingoverlay.reducers';
import { hideOverlayDialog } from '../../overlaydialog/overlaydialog.reducers';
import {
    showEmptyGuideScreen,
    showGuideScreen,
    showPreviousScreen,
} from '../../screens/screens.reducers';
import { hideSpinner } from '../../spinner/spinner.reducers';
import { setCurrentActiveIndexAction } from '../../videoplayerui/infobanner/infobanner.actions';
import {
    hideFullInfo,
    hideStandardInfo,
    hideTrickPlayMenu,
    showTrickPlayMenu,
} from '../../videoplayerui/infobanner/infobanner.reducers';
import { MediaButtonTypes } from '../../videoplayerui/infobanner/mediabuttontypes';
import { PlaybackState, TrickPlayType } from '../../videoplayerui/infobanner/PlaybackTypes';
import { hideOneLineGuide } from '../../videoplayerui/onelineguide/onelineguide.reducers';
import { onKeyBackAudioTrackOverlay } from '../overlays/audiotrackoverlay/audiotrackoverlay.reducers';
import { showDeleteRecordingOverlay } from '../overlays/deleterecording/deleterecording.reducers';
import { onKeyBackSubtitleAndCCLanguageOverlay } from '../overlays/subtitleandcclanguageoverlay/subtitleandcclanguageoverlay.reducers';
import { hideVideoDebugOverlay } from '../videodebugoverlay/videodebugoverlay.reducers';
import {
    hideStillThereOsdAction,
    videoPlayerIsMediaButtonPressedAction,
    videoPlayerSetPlaybackStateAction,
    videoPlayerToggleIsPlayingAction,
} from '../videoplayer.actions';
import {
    disableStillThereTimer,
    handleAdvance,
    handleFastForward,
    handleReplay,
    handleRewind,
    hideNotSupportedText,
    resetPlaybackParamsVideoplayer,
    resetStillThereTimeout,
} from '../videoplayer.reducers';

const TAG = 'videoplayernavigation';
const services = new VodInteractor();

export const isPlaybackNotStarted = (): boolean => {
    const oVideoPlaybackController = VideoPlaybackController.getInstance();

    if (!oVideoPlaybackController.isPlaybackStarted()) {
        return true;
    }
    return false;
};

export const isPlayPauseRestricted = (trickplayType: TrickPlayType) => (dispatch: Function) => {
    let isRestricted = false;
    const oVideoPlaybackController = VideoPlaybackController.getInstance();

    // TODO: Network restriction should be fixed in this [KST-6356] JIRA.
    if (isPlaybackNotStarted() || !oVideoPlaybackController.isNetworkConnected())
        isRestricted = true;
    else if (
        !oVideoPlaybackController.trickModeRestrictions()?.allowPause &&
        oVideoPlaybackController.isTrickplayEnabled()
    ) {
        isRestricted = false;
    } else if (!oVideoPlaybackController.trickModeRestrictions()?.allowPause) {
        dispatch(
            videoPlayerIsMediaButtonPressedAction({
                type: TrickPlayType.PAUSE,
                isMediaButtonPressed: true,
            }),
        );
        isRestricted = true;
    }
    if (!isRestricted) {
        oVideoPlaybackController.getTrickplayControl().stopScrubbing();
        if (trickplayType === TrickPlayType.PAUSE) {
            VideoPlaybackController.getInstance().pause(true);
            dispatch(videoPlayerToggleIsPlayingAction(false));
            dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.PAUSE));
            VuLogger.logTrickPlayEvent(
                VuLogger.TrickplayMode.PAUSE,
                oVideoPlaybackController.getPlayerCurrentTime(),
            );
            dispatch(disableStillThereTimer());
        } else if (trickplayType === TrickPlayType.PLAY) {
            VideoPlaybackController.getInstance().resume();
            dispatch(videoPlayerToggleIsPlayingAction(true));
            dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.PLAY));
            VuLogger.logTrickPlayEvent(
                VuLogger.TrickplayMode.PLAY,
                oVideoPlaybackController.getPlayerCurrentTime(),
            );
            dispatch(resetStillThereTimeout(true));
        }
    }
};

export const handlePlayPause = (trickplayType: TrickPlayType) => (dispatch: Function) => {
    dispatch(setCurrentActiveIndexAction(MediaButtonTypes.PLAY_PAUSE_BUTTON_INDEX));
    dispatch(isPlayPauseRestricted(trickplayType));
};

export const setPositionOnBufferThresholdStart = (activeIndex: number) => (dispatch: Function) => {
    dispatch(setCurrentActiveIndexAction(activeIndex));
};

export const handleTrickplay =
    (trickplayType: TrickPlayType, activeIndex: number, isTrickplayTypeEnable?: boolean) =>
    (dispatch: Function) => {
        let isRestricted = false;
        const oVideoPlaybackController = VideoPlaybackController.getInstance();
        const playerCurrentTime = oVideoPlaybackController.getPlayerCurrentTime();
        const { start, end } = oVideoPlaybackController.getSeekRange();
        const isBufferStartReached =
            start === playerCurrentTime || playerCurrentTime - start < BUFFER_THRESHOLD_OFFSET;
        const isBufferEndReached =
            end === playerCurrentTime || end - playerCurrentTime < BUFFER_THRESHOLD_OFFSET;

        if (isPlaybackNotStarted() || !oVideoPlaybackController.isNetworkConnected())
            isRestricted = true;
        else if (!isTrickplayTypeEnable) {
            dispatch(
                videoPlayerIsMediaButtonPressedAction({
                    type: trickplayType,
                    isMediaButtonPressed: true,
                }),
            );
            isRestricted = true;
        } else if (
            (trickplayType === TrickPlayType.FF || trickplayType === TrickPlayType.ADVANCE) &&
            isBufferEndReached
        )
            isRestricted = true;
        else if (
            (trickplayType === TrickPlayType.RW || trickplayType === TrickPlayType.REPLAY) &&
            isBufferStartReached
        )
            isRestricted = true;

        if (!isRestricted) {
            dispatch(setPositionOnBufferThresholdStart(activeIndex));
            switch (trickplayType) {
                case TrickPlayType.RW:
                    dispatch(handleRewind());
                    break;
                case TrickPlayType.REPLAY:
                    dispatch(handleReplay());
                    break;
                case TrickPlayType.FF:
                    dispatch(handleFastForward());
                    break;
                case TrickPlayType.ADVANCE:
                    dispatch(handleAdvance());
                    break;
                default:
            }
        }
    };

export const handleLeftRight =
    (trickplayType: TrickPlayType, activeIndex: number, isTrickplayTypeEnable?: boolean) =>
    (dispatch: Function) => {
        dispatch(showTrickPlayMenu());
        let isRestricted = false;
        const oVideoPlaybackController = VideoPlaybackController.getInstance();
        const { start, end } = oVideoPlaybackController.getSeekRange();
        const playerCurrentTime = oVideoPlaybackController.getPlayerCurrentTime();
        const isBufferEndReached =
            end === playerCurrentTime || end - playerCurrentTime < BUFFER_THRESHOLD_OFFSET;
        const isBufferStartReached =
            start === playerCurrentTime || playerCurrentTime - start < BUFFER_THRESHOLD_OFFSET;

        if (isPlaybackNotStarted() || !oVideoPlaybackController.isNetworkConnected())
            isRestricted = true;
        else if (!isTrickplayTypeEnable) {
            isRestricted = true;
        } else if (trickplayType === TrickPlayType.FF && isBufferEndReached) isRestricted = true;
        else if (trickplayType === TrickPlayType.RW && isBufferStartReached) isRestricted = true;

        if (!isRestricted) {
            dispatch(setPositionOnBufferThresholdStart(activeIndex));
        }
    };

const onChannelChangeVideoPlayer = (response: TChannelV2 | Channel) => (dispatch: Function) => {
    const playerInstance = VideoPlaybackController.getInstance();
    dispatch(hideSpinner());
    dispatch(hideActiveOverlays());
    if (playerInstance.getPlayable()?.playbackContentType !== PlaybackContentType.LIVE) {
        playerInstance.updateLastViewedPosition();
        PlaybackDataModelImpl.getInstance().resetPlaybackData();
    }
    const livePlayable: IPlayable = {
        channel: response,
        playbackContentType: PlaybackContentType.LIVE,
    };
    playerInstance.startPlayback(livePlayable);
    dispatch(hideNotSupportedText());
    dispatch(resetPlaybackParamsVideoplayer());
    VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.CHANNEL_CHANGE);
    QoEAppLogger.logChannelChanged(
        playerInstance.getPlayerStats(),
        playerInstance.getPlayerCurrentTime(),
        playerInstance.getSeekRange(),
    );
    QoEAppLogger.resetProfilePlayingTime();
    QoEAppLogger.stopMediaHealthTimer();
    Log.i(TAG, 'onChannelChangeVideoPlayer');
};

export const onKeyChannelUpVideoPlayer =
    (noOfKeys: number, channelsType: string) => (dispatch: Function) => {
        const channelNumber = VideoPlaybackController.getInstance().getCurrentChannelNumber();
        const getNextChannel = isJumpChannelCcbSwitchEnabled()
            ? services.getNextChannelV2
            : services.getNextChannel;
        getNextChannel(channelNumber, channelsType, noOfKeys)
            .then((response: TChannelV2 | Channel) => {
                QoEAppLogger.setWatchContentEndReason(
                    QoEAppLogger.PlaybackEndReason.USER_TUNED_TO_NEXT_CHANNEL,
                );
                dispatch(onChannelChangeVideoPlayer(response));
                Diagnostics.debug(TAG, `Next Channel Number: ${response.channelNumber}`);
                Log.d(TAG, `Next Channel Number: ${response.channelNumber}`);
            })
            .catch((error) => {
                Diagnostics.error(TAG, `No Next Channel Available:${error}`);
                Log.e(TAG, `No Next Channel Available:${error}`);
            });
    };

export const onKeyChannelDownVideoPlayer =
    (noOfKeysPressed: number, channelsType: string) => (dispatch: Function) => {
        const channelNumber = VideoPlaybackController.getInstance().getCurrentChannelNumber();
        const getPreviousChannel = isJumpChannelCcbSwitchEnabled()
            ? services.getPreviousChannelV2
            : services.getPreviousChannel;
        getPreviousChannel(channelNumber, channelsType, noOfKeysPressed)
            .then((response: TChannelV2 | Channel) => {
                dispatch(onChannelChangeVideoPlayer(response));
                QoEAppLogger.setWatchContentEndReason(
                    QoEAppLogger.PlaybackEndReason.USER_TUNED_TO_PREVIOUS_CHANNEL,
                );
                Diagnostics.debug(TAG, `Previous Channel Number: ${response.channelNumber}`);
                Log.d(TAG, `Previous Channel Number: ${response.channelNumber}`);
            })
            .catch((error) => {
                Diagnostics.error(TAG, `No Previous Channel Available: ${error}`);
                Log.e(TAG, `No Previous Channel Available: ${error}`);
            });
    };

export const hideActiveOverlays = () => (dispatch: Function, getState: Function) => {
    const { isOverlayDialogActive } = getState().overlayDialog;
    Diagnostics.info(TAG, 'hideActiveOverlays');
    Log.i(TAG, 'hideActiveOverlays');
    if (isOverlayDialogActive) dispatch(hideOverlayDialog());
};

export const exitVideoPlayer =
    (exitToGuideScreen: boolean = false) =>
    (dispatch: Function, getState: Function) => {
        const { videoPlayer, videoDebugOverlay, oneLineGuide } = getState();
        const { isVideoDebugOverlayActive } = videoDebugOverlay;
        const { exitPlayerTimer, stillThereTimer } = videoPlayer;
        const { isEmpty } = oneLineGuide;
        const { screenTaskController } = getState().screens;
        if (isVideoDebugOverlayActive) {
            dispatch(hideVideoDebugOverlay());
        }
        if (stillThereTimer) clearTimeout(stillThereTimer);
        if (exitPlayerTimer) clearTimeout(exitPlayerTimer);
        dispatch(hideStillThereOsdAction());
        dispatch(hideTrickPlayMenu());
        dispatch(hideFullInfo());
        dispatch(hideOneLineGuide());
        dispatch(hideActiveOverlays());

        const oVideoPlaybackController = VideoPlaybackController.getInstance();

        clearScheduleRecordingSharedPreference();
        if (isEmpty) {
            dispatch(showEmptyGuideScreen());
        } else if (!exitToGuideScreen) {
            if (
                oVideoPlaybackController.getPlayable()?.playbackContentType !==
                PlaybackContentType.LIVE
            ) {
                oVideoPlaybackController.updateLastViewedPosition();
            }
            oVideoPlaybackController.minimize();
            if (NetworkManager.isNetworkDisconnected()) dispatch(showGuideScreen());
            else dispatch(showPreviousScreen());
        } else {
            // Below condition will force update the last viewed position if NDVR playback is ended
            if (
                screenTaskController === ScreenTaskControllerTypes.DELETE_RECORDING_OVERLAY_TASKS &&
                oVideoPlaybackController.getPlayerCurrentTime() === 0
            ) {
                oVideoPlaybackController.updateLastViewedPosition(undefined, true);
            }
            oVideoPlaybackController.minimize();
            dispatch(showGuideScreen());
        }
    };

export const onKeyBackDefaultVideoPlayer = () => (dispatch: Function) => {
    const playerInstance = VideoPlaybackController.getInstance();
    if (playerInstance.canShowDeleteRecordingOverlay()) {
        dispatch(showDeleteRecordingOverlay());
        return;
    }

    dispatch(onKeyBackVideoPlayer());
};

export const onKeyBackVideoPlayer =
    (exitToGuideScreen: boolean = false) =>
    (dispatch: Function) => {
        const playerInstance = VideoPlaybackController.getInstance();

        if (!DeviceModel.getAppSettings().isVideoWindowEnabled()) {
            QoEAppLogger.setWatchContentEndReason(
                QoEAppLogger.PlaybackEndReason.USER_EXITED_FROM_LIVE_TV,
            );
            QoEAppLogger.logPlaybackStopped(
                playerInstance.getPlayerStats(),
                playerInstance.getPlayerCurrentTime(),
                playerInstance.getSeekRange(),
            );
            QoEAppLogger.resetProfilePlayingTime();
            VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.ENDED_BY_USER);
            if (playerInstance.getPlayable()?.playbackContentType !== PlaybackContentType.LIVE) {
                playerInstance.updateLastViewedPosition();
                PlaybackDataModelImpl.getInstance().resetPlaybackData();
            }
        }

        if (!DeviceModel.inFrame()) {
            if (!DeviceModel.getAppSettings().isVideoWindowEnabled()) {
                VideoPlaybackController.getInstance().unmount();
            }
        }
        Diagnostics.info(TAG, 'onKeyBackVideoPlayer');
        Log.i(TAG, 'onKeyBackVideoPlayer');

        dispatch(exitVideoPlayer(exitToGuideScreen));
    };

export const onKeyLeftVideoPlayer = () => (dispatch: Function) => {
    dispatch(
        handleLeftRight(
            TrickPlayType.RW,
            MediaButtonTypes.REWIND_BUTTON_INDEX,
            VideoPlaybackController.getInstance().trickModeRestrictions()?.allowRewind,
        ),
    );
    Diagnostics.info(TAG, 'onKeyLeftVideoPlayer');
    Log.i(TAG, 'onKeyLeftVideoPlayer');
};

export const onKeyPauseVideoPlayer = () => (dispatch: Function) => {
    Diagnostics.info(TAG, 'onKeyPauseVideoPlayer');
    Log.i(TAG, 'onKeyPauseVideoPlayer');
    dispatch(handlePlayPause(TrickPlayType.PAUSE));
};

export const onKeyPlayVideoPlayer = () => (dispatch: Function) => {
    Diagnostics.info(TAG, 'onKeyPlayVideoPlayer');
    Log.i(TAG, 'onKeyPlayVideoPlayer');
    dispatch(handlePlayPause(TrickPlayType.PLAY));
};

export const onAppResumeVideoPlayer = () => () => {
    Diagnostics.info(TAG, 'onAppResumeVideoPlayer');
    Log.i(TAG, 'onAppResumeVideoPlayer');
};

export const onAppPauseVideoPlayer = () => (dispatch: Function) => {
    Diagnostics.info(TAG, 'onAppPauseVideoPlayer');
    Log.i(TAG, 'onAppPauseVideoPlayer');
    QoEAppLogger.stopMediaHealthTimer();
    VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.VISIBILITY_BEHIND);

    //  If Socu/NDVR playback is in progress on full screen and the application
    // visibility changes to 'onpause', navigate back to the previous  screen.
    if (!VideoPlaybackController.getInstance().isLive()) {
        dispatch(exitVideoPlayer());
        if (!DeviceModel.inFrame()) {
            if (!DeviceModel.getAppSettings().isVideoWindowEnabled()) {
                VideoPlaybackController.getInstance().stop();
                VideoPlaybackController.getInstance().unmount();
            }
        }
        return;
    }
    VideoPlaybackController.getInstance().getTrickplayControl().stopScrubbing();
    dispatch(disableStillThereTimer());
    dispatch(onKeyBackSubtitleAndCCLanguageOverlay(true));
    dispatch(onKeyBackAudioTrackOverlay(true));
    dispatch(hideStandardInfo());
    dispatch(hideFullInfo());
    dispatch(hideTrickPlayMenu());
    dispatch(hideOneLineGuide());
};

export const onKeyStopVideoPlayer = () => (dispatch: Function) => {
    Diagnostics.info(TAG, 'onKeyStopVideoPlayer');
    Log.i(TAG, 'onKeyStopVideoPlayer');
    const oVideoPlaybackController = VideoPlaybackController.getInstance();
    if (
        !oVideoPlaybackController.trickModeRestrictions()?.allowPause &&
        oVideoPlaybackController.isTrickplayEnabled()
    )
        dispatch(isPlayPauseRestricted(TrickPlayType.PLAY));
    else dispatch(isPlayPauseRestricted(TrickPlayType.PAUSE));
};

export const onKeyFastForwardVideoPlayer = () => (dispatch: Function) => {
    Diagnostics.info(TAG, 'onKeyFastForwardVideoPlayer');
    Log.i(TAG, 'onKeyFastForwardVideoPlayer');
    dispatch(
        handleTrickplay(
            TrickPlayType.FF,
            MediaButtonTypes.FORWARD_BUTTON_INDEX,
            VideoPlaybackController.getInstance().trickModeRestrictions()?.allowFastForward,
        ),
    );
};

export const onKeyRewindVideoPlayer = () => (dispatch: Function) => {
    Diagnostics.info(TAG, 'onKeyRewindVideoPlayer');
    Log.i(TAG, 'onKeyRewindVideoPlayer');

    dispatch(
        handleTrickplay(
            TrickPlayType.RW,
            MediaButtonTypes.REWIND_BUTTON_INDEX,
            VideoPlaybackController.getInstance().trickModeRestrictions()?.allowRewind,
        ),
    );
};

export const onKeyReplayVideoPlayer = () => (dispatch: Function) => {
    Diagnostics.info(TAG, 'onKeyReplayVideoPlayer');
    Log.i(TAG, 'onKeyReplayVideoPlayer');
    dispatch(
        handleTrickplay(
            TrickPlayType.REPLAY,
            MediaButtonTypes.REPLAY_BUTTON_INDEX,
            VideoPlaybackController.getInstance().trickModeRestrictions()?.allowRewind,
        ),
    );
};

export const onKeyAdvanceVideoPlayer = () => (dispatch: Function) => {
    Diagnostics.info(TAG, 'onKeyAdvanceVideoPlayer');
    Log.i(TAG, 'onKeyAdvanceVideoPlayer');
    dispatch(
        handleTrickplay(
            TrickPlayType.ADVANCE,
            MediaButtonTypes.ADVANCE_BUTTON_INDEX,
            VideoPlaybackController.getInstance().trickModeRestrictions()?.allowFastForward,
        ),
    );
};

export const onKeyRightVideoPlayer = () => (dispatch: Function) => {
    dispatch(
        handleLeftRight(
            TrickPlayType.FF,
            MediaButtonTypes.FORWARD_BUTTON_INDEX,
            VideoPlaybackController.getInstance().trickModeRestrictions()?.allowFastForward,
        ),
    );
    Diagnostics.info(TAG, 'onKeyRightVideoPlayer');
    Log.i(TAG, 'onKeyRightVideoPlayer');
};
